/**
 * 返回键处理器管理器 - 栈模式
 *
 * 特性：
 * 1. 每个页面维护一个处理器栈（支持多层级返回逻辑）
 * 2. 执行时从栈顶取出处理器执行
 * 3. 处理器返回 true 且 removeOnHandle=true 时，自动弹出该处理器
 * 4. 路由切换时自动清空旧页面的栈
 *
 * 使用场景：
 * - 播放详情页：剧集列表 → 简介详情 → 页面内容 → 导航栏
 * - 搜索页：键盘 → 输入框 → 导航栏
 *
 * 使用方式：
 * ```tsx
 * // 注册处理器（自动入栈）
 * usePageBackHandler('play-detail-page', () => {
 *   closeEpisodeList();
 *   return true; // 已处理，且会自动弹出此处理器
 * }, { removeOnHandle: true });
 *
 * // 或者手动控制
 * pushBackHandler('play-detail-page', () => {
 *   // 处理逻辑
 *   return true;
 * });
 * ```
 */

export type BackHandler = () => boolean | void;

export interface BackHandlerOptions {
  /**
   * 是否在处理后自动移除该处理器
   * true: 处理后自动弹出（适用于临时状态，如弹窗、子页面）
   * false: 保留处理器（适用于持久状态，如页面级处理）
   * 默认: true
   */
  removeOnHandle?: boolean;

  /**
   * 处理器优先级（数字越大优先级越高）
   * 同一页面的多个处理器会按优先级排序
   * 默认: 0
   */
  priority?: number;
}

interface HandlerItem {
  handler: BackHandler;
  options: Required<BackHandlerOptions>;
  id: string; // 用于精确移除
}

class BackHandlerManager {
  // 每个页面维护一个处理器栈
  private handlerStacks: Map<string, HandlerItem[]> = new Map();

  // 当前活跃的页面 ID
  private currentPageId = '';

  // 用于生成唯一 ID
  private idCounter = 0;

  /**
   * 设置当前页面（路由切换时调用）
   * @param pageId 新页面的唯一标识
   */
  setCurrentPage(pageId: string): void {
    if (this.currentPageId !== pageId) {
      console.log(`[BackHandlerManager] 切换页面: ${this.currentPageId} -> ${pageId}`);
      this.currentPageId = pageId;

      // 清空旧页面的栈（可选，如果需要保留可以注释掉）
      // 保留当前页面的栈，清空其他页面
      // this.handlerStacks.forEach((stack, id) => {
      //   if (id !== pageId) {
      //     this.handlerStacks.delete(id);
      //   }
      // });
    }
  }

  /**
   * 获取当前页面 ID
   */
  getCurrentPageId(): string {
    return this.currentPageId;
  }

  /**
   * 压入处理器到栈顶
   * @param pageId 页面唯一标识
   * @param handler 处理器函数
   * @param options 处理器选项
   * @returns 处理器的唯一 ID，用于精确移除
   */
  push(pageId: string, handler: BackHandler, options: BackHandlerOptions = {}): string {
    this.idCounter += 1;
    const handlerId = `handler-${this.idCounter}`;
    const handlerItem: HandlerItem = {
      handler,
      options: {
        removeOnHandle: options.removeOnHandle ?? true,
        priority: options.priority ?? 0,
      },
      id: handlerId,
    };

    if (!this.handlerStacks.has(pageId)) {
      this.handlerStacks.set(pageId, []);
    }

    const stack = this.handlerStacks.get(pageId)!;
    stack.push(handlerItem);

    // 按优先级排序（优先级高的在前面）
    stack.sort((a, b) => b.options.priority - a.options.priority);

    console.log(`[BackHandlerManager] 注册处理器到栈: ${pageId}, 栈深度: ${stack.length}, ID: ${handlerId}`);
    return handlerId;
  }

  /**
   * 从栈顶弹出处理器
   * @param pageId 页面唯一标识
   * @returns 弹出的处理器，如果栈为空则返回 undefined
   */
  pop(pageId: string): HandlerItem | undefined {
    const stack = this.handlerStacks.get(pageId);
    if (!stack || stack.length === 0) {
      return undefined;
    }

    const handler = stack.pop();
    console.log(`[BackHandlerManager] 弹出处理器: ${pageId}, 剩余栈深度: ${stack.length}`);
    return handler;
  }

  /**
   * 精确移除指定的处理器
   * @param pageId 页面唯一标识
   * @param handlerId 处理器 ID
   */
  remove(pageId: string, handlerId: string): void {
    const stack = this.handlerStacks.get(pageId);
    if (!stack) {
      return;
    }

    const index = stack.findIndex((item) => item.id === handlerId);
    if (index !== -1) {
      stack.splice(index, 1);
      console.log(`[BackHandlerManager] 移除处理器: ${pageId}, ID: ${handlerId}, 剩余栈深度: ${stack.length}`);
    }
  }

  /**
   * 查看栈顶处理器（不弹出）
   * @param pageId 页面唯一标识
   */
  peek(pageId: string): HandlerItem | undefined {
    const stack = this.handlerStacks.get(pageId);
    if (!stack || stack.length === 0) {
      return undefined;
    }
    return stack[stack.length - 1];
  }

  /**
   * 获取栈的深度
   * @param pageId 页面唯一标识
   */
  getStackDepth(pageId: string): number {
    const stack = this.handlerStacks.get(pageId);
    return stack ? stack.length : 0;
  }

  /**
   * 执行栈顶处理器
   * @param pageId 页面唯一标识
   * @returns true 表示已处理，false 表示未处理或没有处理器
   */
  execute(pageId: string): boolean {
    const stack = this.handlerStacks.get(pageId);
    if (!stack || stack.length === 0) {
      console.log(`[BackHandlerManager] 页面 ${pageId} 没有处理器`);
      return false;
    }

    // 获取栈顶处理器
    const topHandler = stack[stack.length - 1];
    console.log(`[BackHandlerManager] 执行栈顶处理器: ${pageId}, 栈深度: ${stack.length}, ID: ${topHandler.id}`);

    const result = topHandler.handler();
    const handled = result === true;

    // 如果处理器返回 true 且配置了自动移除，则弹出该处理器
    if (handled && topHandler.options.removeOnHandle) {
      stack.pop();
      console.log(`[BackHandlerManager] 处理器已自动移除: ${pageId}, 剩余栈深度: ${stack.length}`);
    }

    return handled;
  }

  /**
   * 清空指定页面的处理器栈
   * @param pageId 页面唯一标识
   */
  clearPage(pageId: string): void {
    console.log(`[BackHandlerManager] 清空页面处理器栈: ${pageId}`);
    this.handlerStacks.delete(pageId);
  }

  /**
   * 清空所有处理器栈
   */
  clear(): void {
    console.log('[BackHandlerManager] 清空所有处理器栈');
    this.handlerStacks.clear();
    this.currentPageId = '';
  }
}

export const backHandlerManager = new BackHandlerManager();
